const hasOwn = Object.prototype.hasOwnProperty;

type OmitUndefinedEntries<T extends object> = {
  [K in keyof T as T[K] extends undefined ? never : K]: Exclude<
    T[K],
    undefined
  >;
};

/**
 * Returns a shallow copy of `object` with all of its undefined key values
 * removed. The returned TS type will also have the undefined keys omitted.
 *
 * @param object Subject object
 * @returns A shallow copy of `object` with undefined property values removed
 */
export function omitUndefinedEntries<TObject extends object>(
  object: TObject
): OmitUndefinedEntries<TObject> {
  const result: Record<string, unknown> = {};

  // this function is called very frequently, so we're doing a bit of micro-
  // optimization to avoid Object.entries/destructuring allocations
  for (const key in object) {
    // mimic Object.entries behavior by skipping inherited properties
    if (!hasOwn.call(object, key)) {
      continue;
    }

    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
    const value = object[key as keyof TObject];

    if (value !== undefined) {
      result[key] = value;
    }
  }

  // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
  return result as OmitUndefinedEntries<TObject>;
}
